/**
 * Branch → Schedule → Employee Editor (4-week grid).
 *
 * Mirrors the Mindbody employee working-hours screen the user shared: four
 * stacked week cards, each with 7 day rows (Workday / Day Off / Training
 * Day) + Work Start/End + Lunch Start/End + "Copy Previous Day". At the
 * bottom: Lock to Room (per week), an *Ignore Schedule Conflicts* checkbox,
 * and Cancel / Save & Back / Save actions.
 *
 * When the operator saves, the backend detects appointment conflicts and
 * returns HTTP 409 with a list of them. We surface those inline as an alert
 * banner. If the operator ticks *Ignore Schedule Conflicts* and saves again,
 * the override is written anyway — the employee's day becomes greyed-out on
 * the calendar but existing appointments remain so the operator can drag
 * them onto another practitioner.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertCircle, CalendarDays, Repeat, Trash2 } from "lucide-react";

const WEEKDAY_LABELS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const MODES = [
  { value: "workday", label: "Workday" },
  { value: "day_off", label: "Day Off" },
  { value: "training_day", label: "Training Day" },
  // Feb 2026 — Leave categories. Booking engine treats them like a
  // day off (specialist column hidden on the calendar) but the
  // specific label is preserved on save so Reports can count
  // PH/AL/SL per specialist by name.
  { value: "public_holiday", label: "Public Holiday (PH)" },
  { value: "annual_leave", label: "Annual Leave (AL)" },
  { value: "sick_leave", label: "Sick Leave (SL)" },
];

// Every mode that acts as a "not working" day — booking engine hides
// the column and the work / lunch time inputs freeze. Kept in sync
// with `NON_WORKING_MODES` on the backend.
const NON_WORKING_MODES = new Set([
  "day_off", "training_day", "closed",
  "public_holiday", "annual_leave", "sick_leave",
]);

// Map a stored mode value into one of the dropdown options.
// Only the legacy `"off"` alias collapses to `day_off` — every new
// leave label (PH/AL/SL) is preserved verbatim so the operator sees
// the exact category they picked.
const displayMode = (m) => (m === "off" ? "day_off" : (m || "workday"));

// 15-minute time-picker options, 06:00 → 22:00 — enough for spa hours.
const TIME_OPTIONS = (() => {
  const opts = [{ value: "", label: "—" }];
  for (let h = 6; h <= 22; h++) {
    for (const m of [0, 15, 30, 45]) {
      const v = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      opts.push({ value: v, label: v });
    }
  }
  return opts;
})();

// -- Local date helpers -------------------------------------------------------
function parseYmd(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}
function fmtLong(dObj) {
  return dObj.toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone: "UTC" });
}
function fmtShort(dObj) {
  return dObj.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}
function addDays(dObj, days) {
  const nd = new Date(dObj.getTime());
  nd.setUTCDate(nd.getUTCDate() + days);
  return nd;
}
function mondayOf(dObj) {
  const dow = dObj.getUTCDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  return addDays(dObj, offset);
}
function toYmd(dObj) {
  return dObj.toISOString().slice(0, 10);
}
// dd/mm/yy <-> YYYY-MM-DD for the Regular Schedule starting-date field.
function ymdToDmy(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}
function dmyToYmd(s) {
  const m = (s || "").trim().match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (!m) return null;
  const d = parseInt(m[1], 10), mo = parseInt(m[2], 10);
  let y = m[3];
  y = y.length === 2 ? 2000 + parseInt(y, 10) : parseInt(y, 10);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return toYmd(dt);
}
// Normalise any week-shaped source (default template / saved pattern) into
// the editor's 7-day state.
function normalizeWeek(src) {
  const out = {};
  for (const { key } of WEEKDAY_LABELS) {
    const d = src?.[key] || {};
    out[key] = {
      mode: d.mode === "off" ? "day_off" : (d.mode || "workday"),
      work_start: d.work_start || "",
      work_end: d.work_end || "",
      lunch_start: d.lunch_start || "",
      lunch_end: d.lunch_end || "",
    };
  }
  return out;
}

const REGULAR = "__regular__";

// ---------------------------------------------------------------------------

export default function BranchScheduleEditor() {
  const { bid, uid } = useParams();
  const nav = useNavigate();
  const [search, setSearch] = useSearchParams();

  const initialWeek = useMemo(() => {
    const raw = search.get("week");
    if (raw) return raw;
    return toYmd(mondayOf(new Date()));
  }, [search]);

  const [weekStart, setWeekStart] = useState(initialWeek);
  const [staff, setStaff] = useState(null);
  const [weeks, setWeeks] = useState([]); // [{week_start, days:{mon:...}, lock_to_room_id}]
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ignoreConflicts, setIgnoreConflicts] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [conflictAsk, setConflictAsk] = useState(null);
  const [savedOnce, setSavedOnce] = useState(false);

  // ---- Regular Schedule mode ------------------------------------------------
  const [isRegular, setIsRegular] = useState(false);
  const [regItems, setRegItems] = useState([]);          // saved patterns, newest first
  const [regEffective, setRegEffective] = useState(toYmd(new Date()));  // YYYY-MM-DD
  const [regDateText, setRegDateText] = useState(ymdToDmy(toYmd(new Date())));
  const [regDays, setRegDays] = useState(normalizeWeek(null));
  const [regLock, setRegLock] = useState(null);
  const [regLoading, setRegLoading] = useState(false);
  const [regCalOpen, setRegCalOpen] = useState(false);

  const loadRegular = useCallback(async () => {
    setRegLoading(true);
    try {
      const r = await staffApi.get(`/branches/${bid}/staff-users/${uid}/regular-schedule`);
      const items = r.data.items || [];
      setRegItems(items);
      if (items.length) {
        const latest = items[0];
        setRegEffective(latest.effective_from);
        setRegDateText(ymdToDmy(latest.effective_from));
        setRegDays(normalizeWeek(latest.days));
        setRegLock(latest.lock_to_room_id || null);
      } else {
        setRegDays(normalizeWeek(r.data.default_week));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRegLoading(false); }
  }, [bid, uid]);
  useEffect(() => { if (isRegular) { setConflicts([]); loadRegular(); } }, [isRegular, loadRegular]);

  const commitRegDateText = () => {
    const ymd = dmyToYmd(regDateText);
    if (!ymd) { toast.error("Enter the starting date as dd/mm/yy"); setRegDateText(ymdToDmy(regEffective)); return; }
    setRegEffective(ymd);
    setRegDateText(ymdToDmy(ymd));
  };
  const loadPattern = (item) => {
    setRegEffective(item.effective_from);
    setRegDateText(ymdToDmy(item.effective_from));
    setRegDays(normalizeWeek(item.days));
    setRegLock(item.lock_to_room_id || null);
  };
  const deletePattern = async (item) => {
    if (!window.confirm(`Delete the regular schedule starting ${ymdToDmy(item.effective_from)}?`)) return;
    try { await staffApi.delete(`/regular-schedules/${item.id}`); toast.success("Pattern deleted"); loadRegular(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  function updateRegDay(dayKey, patch) {
    setRegDays((d) => ({ ...d, [dayKey]: { ...(d[dayKey] || {}), ...patch } }));
  }
  function copyPrevRegDay(dayIdx) {
    if (dayIdx === 0) return;
    setRegDays((d) => ({ ...d, [WEEKDAY_LABELS[dayIdx].key]: { ...(d[WEEKDAY_LABELS[dayIdx - 1].key] || {}) } }));
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [schedRes, roomsRes] = await Promise.all([
        staffApi.get(`/branches/${bid}/staff-users/${uid}/schedule`, { params: { week_start: weekStart, weeks: 4 } }),
        staffApi.get(`/branches/${bid}/rooms`),
      ]);
      setStaff(schedRes.data.staff);
      setWeeks(schedRes.data.weeks);
      setRooms(roomsRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [bid, uid, weekStart]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const cur = search.get("week");
    if (cur !== weekStart) {
      const next = new URLSearchParams(search);
      next.set("week", weekStart);
      setSearch(next, { replace: true });
    }
  }, [weekStart, search, setSearch]);

  // Week picker options — same +/- window as the landing page.
  const weekOptions = useMemo(() => {
    const anchor = parseYmd(weekStart);
    const opts = [];
    for (let i = -1; i <= 6; i++) {
      const wk = addDays(anchor, i * 7);
      opts.push({
        value: toYmd(wk),
        label: `${fmtShort(wk)} - ${fmtShort(addDays(wk, 27))}`,
      });
    }
    return opts;
  }, [weekStart]);

  const rangeLabel = weeks.length
    ? `${fmtShort(parseYmd(weeks[0].week_start))} - ${fmtShort(parseYmd(weeks[weeks.length - 1].week_end))}`
    : "";

  // Mutation helpers -----------------------------------------------------------
  function updateDay(weekIdx, dayKey, patch) {
    setWeeks((w) => {
      const next = [...w];
      const wk = { ...next[weekIdx], days: { ...next[weekIdx].days } };
      wk.days[dayKey] = { ...(wk.days[dayKey] || {}), ...patch };
      next[weekIdx] = wk;
      return next;
    });
  }

  function copyPreviousDay(weekIdx, dayIdx) {
    if (dayIdx === 0) return;
    setWeeks((w) => {
      const next = [...w];
      const wk = { ...next[weekIdx], days: { ...next[weekIdx].days } };
      const prevKey = WEEKDAY_LABELS[dayIdx - 1].key;
      const curKey = WEEKDAY_LABELS[dayIdx].key;
      wk.days[curKey] = { ...(wk.days[prevKey] || {}) };
      next[weekIdx] = wk;
      return next;
    });
  }

  function updateLockToRoom(weekIdx, roomId) {
    setWeeks((w) => {
      const next = [...w];
      next[weekIdx] = { ...next[weekIdx], lock_to_room_id: roomId || null };
      return next;
    });
  }

  // Save -----------------------------------------------------------------------
  async function save({ backToList, force } = {}) {
    setSaving(true);
    setConflicts([]);
    try {
      if (isRegular) {
        await staffApi.put(`/branches/${bid}/staff-users/${uid}/regular-schedule`, {
          effective_from: regEffective,
          lock_to_room_id: regLock || null,
          days: Object.fromEntries(
            WEEKDAY_LABELS.map(({ key }) => [
              key,
              {
                mode: regDays?.[key]?.mode || "workday",
                work_start: regDays?.[key]?.work_start || "",
                work_end: regDays?.[key]?.work_end || "",
                lunch_start: regDays?.[key]?.lunch_start || "",
                lunch_end: regDays?.[key]?.lunch_end || "",
              },
            ]),
          ),
          ignore_conflicts: ignoreConflicts || !!force,
        });
        toast.success(`Regular schedule saved — starts ${ymdToDmy(regEffective)}.`);
        // Iter-260 — belt-and-braces same-tab live refresh: notify any
        // BranchCalendar mounted in this same tab even before the WS
        // hub round-trip completes, so the operator sees Sat/Sun's
        // newly-on-duty column repaint instantly.
        try {
          window.dispatchEvent(new CustomEvent("cocoon:schedule-updated", {
            detail: { staff_id: uid, branch_id: bid, kind: "regular" },
          }));
        } catch { /* noop */ }
        setSavedOnce(true);
        if (backToList) nav(`/staff/branch/${bid}/schedule?week=${weekStart}`);
        else loadRegular();
        return;
      }
      const payload = {
        weeks: weeks.map((w) => ({
          week_start: w.week_start,
          lock_to_room_id: w.lock_to_room_id || null,
          days: Object.fromEntries(
            WEEKDAY_LABELS.map(({ key }) => [
              key,
              {
                mode: w.days?.[key]?.mode || "workday",
                work_start: w.days?.[key]?.work_start || "",
                work_end: w.days?.[key]?.work_end || "",
                lunch_start: w.days?.[key]?.lunch_start || "",
                lunch_end: w.days?.[key]?.lunch_end || "",
              },
            ]),
          ),
        })),
        ignore_conflicts: ignoreConflicts || !!force,
      };
      const r = await staffApi.put(`/branches/${bid}/staff-users/${uid}/schedule`, payload);
      toast.success(
        r.data.ignored_conflicts
          ? "Schedule saved (existing appointments kept — drag them to another team member)."
          : "Schedule saved.",
      );
      setSavedOnce(true);
      if (backToList) nav(`/staff/branch/${bid}/schedule?week=${weekStart}`);
      else load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.conflicts) {
        setConflicts(detail.conflicts);
        setConflictAsk({ conflicts: detail.conflicts, backToList });
        // Scroll the alert into view.
        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <StaffShell>
      <div className="p-6 md:p-8 max-w-[1000px] mx-auto">
        <div className="border-b border-neutral-300 pb-4 mb-6" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(230,190,190,0.15) 8px, rgba(230,190,190,0.15) 12px)" }}>
          <h1 className="font-editorial text-3xl md:text-4xl" data-testid="schedule-editor-title">
            {staff?.full_name || "Employee"} : Working Hours : {isRegular ? "Regular Schedule" : rangeLabel}
          </h1>
        </div>

        {conflicts.length > 0 && (
          <div className="mb-6 border border-rose-300 bg-rose-50 rounded-md p-4" data-testid="schedule-conflict-alert">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="text-rose-700 font-medium mb-1">There were some conflicts with already existing appointments.</div>
                <ul className="list-disc pl-4 text-neutral-700 space-y-0.5" data-testid="schedule-conflict-list">
                  {conflicts.slice(0, 8).map((c) => (
                    <li key={c.appointment_id}>
                      <span className="font-medium">{c.date} · {c.time}</span> — {c.customer_name || "Client"} · {c.service_name} <span className="italic text-neutral-500">({c.reason})</span>
                    </li>
                  ))}
                  {conflicts.length > 8 && <li className="italic text-neutral-500">+{conflicts.length - 8} more…</li>}
                </ul>
                <div className="text-neutral-700 mt-2">Tick <b>Ignore Schedule Conflicts</b> below and save again to override.</div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <label className="text-sm text-neutral-700">Select Week:</label>
          <Select
            value={isRegular ? REGULAR : weekStart}
            onValueChange={(v) => { if (v === REGULAR) setIsRegular(true); else { setIsRegular(false); setWeekStart(v); } }}
          >
            <SelectTrigger className="w-[220px]" data-testid="schedule-editor-week-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={REGULAR} data-testid="schedule-regular-option">
                <span className="flex items-center gap-2"><Repeat className="w-3.5 h-3.5" /> Regular Schedule</span>
              </SelectItem>
              {weekOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isRegular && (
            <div className="flex items-center gap-2" data-testid="regular-start-row">
              <label className="text-sm text-neutral-700 ml-2">Starting date:</label>
              <Input
                value={regDateText}
                onChange={(e) => setRegDateText(e.target.value)}
                onBlur={commitRegDateText}
                onKeyDown={(e) => e.key === "Enter" && commitRegDateText()}
                placeholder="dd/mm/yy"
                className="w-[110px] h-9"
                data-testid="regular-start-input"
              />
              <Popover open={regCalOpen} onOpenChange={setRegCalOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-9" data-testid="regular-start-calendar-btn">
                    <CalendarDays className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" data-testid="regular-start-calendar">
                  <Calendar
                    mode="single"
                    selected={parseYmd(regEffective)}
                    onSelect={(d) => {
                      if (!d) return;
                      const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                      setRegEffective(ymd);
                      setRegDateText(ymdToDmy(ymd));
                      setRegCalOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        {isRegular && regItems.length > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap text-xs" data-testid="regular-history">
            <span className="text-neutral-500 uppercase tracking-wider text-[10px]">Saved patterns:</span>
            {regItems.map((it) => (
              <span
                key={it.id}
                className={
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 cursor-pointer " +
                  (it.effective_from === regEffective ? "border-emerald-400 bg-emerald-50 text-emerald-900" : "border-neutral-300 bg-white hover:bg-neutral-50")
                }
                onClick={() => loadPattern(it)}
                data-testid={`regular-pattern-${it.effective_from}`}
              >
                starts {ymdToDmy(it.effective_from)}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); deletePattern(it); }}
                  className="text-neutral-400 hover:text-rose-600"
                  data-testid={`regular-pattern-delete-${it.effective_from}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {isRegular ? (
          regLoading ? (
            <div className="text-center py-16 text-neutral-500">Loading…</div>
          ) : (
            <Card className="mb-4 border-neutral-300" data-testid="regular-schedule-card">
              <CardContent className="p-4">
                <div className="grid grid-cols-[160px_1fr_1fr] gap-4 mb-2 items-end">
                  <div>
                    <div className="font-medium flex items-center gap-1.5"><Repeat className="w-4 h-4 text-emerald-700" /> Every week</div>
                    <div className="text-xs text-neutral-500">from {ymdToDmy(regEffective)}</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium mb-1">Work Hours</div>
                    <div className="flex text-xs text-neutral-500 justify-around"><span>Start Time</span><span>End Time</span></div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium mb-1">Lunch</div>
                    <div className="flex text-xs text-neutral-500 justify-around"><span>Start Time</span><span>End Time</span></div>
                  </div>
                </div>

                {WEEKDAY_LABELS.map(({ key, label }, dayIdx) => {
                  const d = regDays?.[key] || {};
                  const disabled = NON_WORKING_MODES.has(d.mode);
                  return (
                    <div key={key} className="grid grid-cols-[160px_1fr_1fr_120px] gap-4 items-center py-1.5 border-t border-neutral-100" data-testid={`regular-day-${key}`}>
                      <div className="text-sm">{label}</div>
                      <div className="grid grid-cols-3 gap-2 items-center">
                        <Select value={displayMode(d.mode)} onValueChange={(v) => updateRegDay(key, { mode: v })}>
                          <SelectTrigger className="h-8" data-testid={`regular-mode-${key}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <TimeSelect disabled={disabled} value={d.work_start || ""} onChange={(v) => updateRegDay(key, { work_start: v })} testId={`regular-workstart-${key}`} />
                        <TimeSelect disabled={disabled} value={d.work_end || ""} onChange={(v) => updateRegDay(key, { work_end: v })} testId={`regular-workend-${key}`} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 items-center">
                        <TimeSelect disabled={disabled} value={d.lunch_start || ""} onChange={(v) => updateRegDay(key, { lunch_start: v })} testId={`regular-lunchstart-${key}`} />
                        <TimeSelect disabled={disabled} value={d.lunch_end || ""} onChange={(v) => updateRegDay(key, { lunch_end: v })} testId={`regular-lunchend-${key}`} />
                      </div>
                      <div className="text-right">
                        {dayIdx > 0 && (
                          <button
                            type="button"
                            onClick={() => copyPrevRegDay(dayIdx)}
                            className="text-xs text-rose-600 hover:underline"
                            data-testid={`regular-copy-prev-${key}`}
                          >
                            Copy Previous Day
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div className="grid grid-cols-[160px_1fr] gap-4 items-center pt-2 mt-2 border-t border-neutral-100">
                  <div className="text-sm text-neutral-700">Lock to Room:</div>
                  <Select value={regLock || "__none__"} onValueChange={(v) => setRegLock(v === "__none__" ? null : v)}>
                    <SelectTrigger className="h-8 w-[240px]" data-testid="regular-lock-room">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )
        ) : loading ? (
          <div className="text-center py-16 text-neutral-500">Loading…</div>
        ) : (
          <>
            {weeks.map((wk, weekIdx) => {
              const wkStart = parseYmd(wk.week_start);
              const wkEnd = addDays(wkStart, 6);
              return (
                <Card key={wk.week_start} className="mb-4 border-neutral-300" data-testid={`schedule-week-card-${weekIdx}`}>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-[160px_1fr_1fr] gap-4 mb-2 items-end">
                      <div>
                        <div className="font-medium">Week {weekIdx + 1}</div>
                        <div className="text-xs text-neutral-500">{fmtShort(wkStart)} - {fmtShort(wkEnd)}</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium mb-1">Work Hours</div>
                        <div className="flex text-xs text-neutral-500 justify-around"><span>Start Time</span><span>End Time</span></div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium mb-1">Lunch</div>
                        <div className="flex text-xs text-neutral-500 justify-around"><span>Start Time</span><span>End Time</span></div>
                      </div>
                    </div>

                    {WEEKDAY_LABELS.map(({ key, label }, dayIdx) => {
                      const d = wk.days?.[key] || {};
                      const dateObj = addDays(wkStart, dayIdx);
                      const disabled = NON_WORKING_MODES.has(d.mode);
                      return (
                        <div key={key} className="grid grid-cols-[160px_1fr_1fr_120px] gap-4 items-center py-1.5 border-t border-neutral-100" data-testid={`schedule-day-${weekIdx}-${key}`}>
                          <div className="text-sm">
                            {label} <span className="text-neutral-500">({fmtLong(dateObj)})</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 items-center">
                            <Select value={displayMode(d.mode)} onValueChange={(v) => updateDay(weekIdx, key, { mode: v })}>
                              <SelectTrigger className="h-8" data-testid={`schedule-mode-${weekIdx}-${key}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <TimeSelect
                              disabled={disabled}
                              value={d.work_start || ""}
                              onChange={(v) => updateDay(weekIdx, key, { work_start: v })}
                              testId={`schedule-workstart-${weekIdx}-${key}`}
                            />
                            <TimeSelect
                              disabled={disabled}
                              value={d.work_end || ""}
                              onChange={(v) => updateDay(weekIdx, key, { work_end: v })}
                              testId={`schedule-workend-${weekIdx}-${key}`}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2 items-center">
                            <TimeSelect
                              disabled={disabled}
                              value={d.lunch_start || ""}
                              onChange={(v) => updateDay(weekIdx, key, { lunch_start: v })}
                              testId={`schedule-lunchstart-${weekIdx}-${key}`}
                            />
                            <TimeSelect
                              disabled={disabled}
                              value={d.lunch_end || ""}
                              onChange={(v) => updateDay(weekIdx, key, { lunch_end: v })}
                              testId={`schedule-lunchend-${weekIdx}-${key}`}
                            />
                          </div>
                          <div className="text-right">
                            {dayIdx > 0 && (
                              <button
                                type="button"
                                onClick={() => copyPreviousDay(weekIdx, dayIdx)}
                                className="text-xs text-rose-600 hover:underline"
                                data-testid={`schedule-copy-prev-${weekIdx}-${key}`}
                              >
                                Copy Previous Day
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    <div className="grid grid-cols-[160px_1fr] gap-4 items-center pt-2 mt-2 border-t border-neutral-100">
                      <div className="text-sm text-neutral-700">Lock to Room:</div>
                      <Select
                        value={wk.lock_to_room_id || "__none__"}
                        onValueChange={(v) => updateLockToRoom(weekIdx, v === "__none__" ? null : v)}
                      >
                        <SelectTrigger className="h-8 w-[240px]" data-testid={`schedule-lock-room-${weekIdx}`}>
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">—</SelectItem>
                          {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </>
        )}

        <div className="flex flex-col items-center gap-4 mt-8">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={ignoreConflicts}
              onCheckedChange={(v) => setIgnoreConflicts(!!v)}
              data-testid="schedule-ignore-conflicts"
            />
            <span>Ignore Schedule Conflicts</span>
          </label>
          <div className="flex items-center gap-4">
            <Link
              to={`/staff/branch/${bid}/schedule?week=${weekStart}`}
              className="text-rose-600 hover:underline text-sm"
              data-testid="schedule-editor-cancel"
            >
              Cancel
            </Link>
            <Button
              onClick={() => save({ backToList: true })}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-6"
              data-testid="schedule-editor-save-and-back"
            >
              Save and Back to Schedules
            </Button>
            <Button
              onClick={() => save()}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-6"
              data-testid="schedule-editor-save"
            >
              Save
            </Button>
          </div>
        </div>
        {/* "This employee has existing appointments" — explicit yes/no before
            a day-off (or shrunk hours) override lands on top of bookings. */}
        <Dialog open={!!conflictAsk} onOpenChange={(v) => { if (!v) setConflictAsk(null); }}>
          <DialogContent className="max-w-lg" data-testid="dayoff-conflict-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-rose-600" /> This employee has existing appointments
              </DialogTitle>
              <DialogDescription>
                The schedule change conflicts with these bookings (paid or unpaid):
              </DialogDescription>
            </DialogHeader>
            <div className="border border-border rounded-md divide-y divide-border max-h-64 overflow-y-auto" data-testid="dayoff-conflict-list">
              {(conflictAsk?.conflicts || []).map((c) => (
                <div key={c.appointment_id} className="px-3 py-2 text-sm">
                  <span className="font-medium">{c.date} · {c.time}</span> — {c.customer_name || "Client"} · {c.service_name}
                  <span className="italic text-neutral-500 text-xs ml-1">({c.reason})</span>
                </div>
              ))}
            </div>
            <p className="text-sm font-medium">Do you want to proceed a day off for this team?</p>
            <p className="text-xs text-muted-foreground">
              Yes — the schedule is saved, these appointments stay on the calendar in grey and can be
              dragged onto another available team member.
            </p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConflictAsk(null)} data-testid="dayoff-conflict-no">No, keep schedule</Button>
              <Button
                className="bg-rose-600 hover:bg-rose-700 text-white"
                disabled={saving}
                onClick={() => { const b = conflictAsk?.backToList; setConflictAsk(null); save({ backToList: b, force: true }); }}
                data-testid="dayoff-conflict-yes"
              >
                Yes, proceed with day off
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </StaffShell>
  );
}

// Compact time <Select>. Kept local since it's tightly coupled to the 15-min
// grid used only here.
function TimeSelect({ value, onChange, disabled, testId }) {
  return (
    <Select value={value || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? "" : v)} disabled={disabled}>
      <SelectTrigger className="h-8" data-testid={testId}>
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {TIME_OPTIONS.map((t) => (
          <SelectItem key={t.value || "empty"} value={t.value || "__none__"}>{t.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
